// ============================================
// FILE: src/components/HomeScreen.jsx
// ============================================

import React from 'react';
import { Search, Bell, MapPin, Menu, Heart, Gift, Package, Plus, ShoppingCart } from 'lucide-react';

import LocationModal from './modals/LocationModal';
import { banners, locations, subscriptionPlans } from '../data/products';

 
const HomeScreen = ({
  searchQuery = '',
  setSearchQuery,
  setShowNotifications,
  setShowLocationModal,
  showLocationModal,
  selectedLocation,
  setSelectedLocation,
  currentBanner,
  setCurrentBanner,
  addToCart,
  setShowSubscription,
  products,
  filteredProducts,
  cart = [],
  getCartCount,
  openCheckout,
  userProfile = {},
  notifications = [],
  favorites = [],
  setShowFavorites,
  setActiveTab
}) => {

  return (
    <div className="pb-20">
      {/* Interactive Header */}
      <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white pb-6 rounded-b-3xl shadow-lg">
        {/* Top Bar */}
        <div className="flex items-center justify-between px-4 pt-4 pb-3">
          <button className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-all">
            <Menu size={24} />
          </button>
          
          <div className="flex items-center gap-3">
            <button 
              onClick={() => setShowNotifications(true)}
              className="relative p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-all"
            >
              <Bell size={24} />
              {notifications.filter(n => n.unread).length > 0 && (
                <span className="absolute top-1 right-1 min-w-[18px] h-[18px] bg-red-500 rounded-full border-2 border-white flex items-center justify-center">
                  <span className="text-[10px] font-bold text-white">
                    {notifications.filter(n => n.unread).length}
                  </span>
                </span>
              )}
            </button>
            
            <button 
              onClick={() => setShowFavorites(true)}
              className="relative p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-all"
            >
              <Heart size={24} />
              {favorites.length > 0 && (
                <span className="absolute top-1 right-1 min-w-[18px] h-[18px] bg-pink-500 rounded-full border-2 border-white flex items-center justify-center">
                  <span className="text-[10px] font-bold text-white">
                    {favorites.length}
                  </span>
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Location & Greeting */}
     
        
        <div className="px-4 mb-3">
          <p className="text-blue-100 text-sm mb-1">
            Good Morning! 👋 {userProfile?.name && <span className="font-semibold">{userProfile.name}</span>}
          </p>
          <h1 className="text-2xl font-bold mb-3">Fresh Milk Delivery</h1>
          
          <button 
            onClick={() => setShowLocationModal(true)}
            className="flex items-center gap-2 bg-white bg-opacity-20 px-3 py-2 rounded-lg hover:bg-opacity-30 transition-all"
          >
            <MapPin size={16} />
            <span className="text-sm font-medium">{selectedLocation}</span>
            <span className="text-xs">▼</span>
          </button>
        </div>

        {/* Search Bar */}
        <div className="px-4">
          <div className="bg-white rounded-full flex items-center px-4 py-3 shadow-md">
            <Search className="text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Search for milk, paneer, curd..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="flex-1 ml-3 outline-none text-gray-800"
            />
          </div>
        </div>

        {/* Quick Action Buttons */}
        <div className="px-4 mt-4 flex gap-3 overflow-x-auto">
          <button 
            onClick={() => {
              // Scroll to banner section which shows offers
              const bannerElement = document.querySelector('.banner-carousel');
              if (bannerElement) {
                bannerElement.scrollIntoView({ behavior: 'smooth' });
              }
            }}
            className="flex items-center gap-2 bg-white bg-opacity-20 px-4 py-2 rounded-full whitespace-nowrap hover:bg-opacity-30 transition-all"
          >
            <Gift size={16} />
            <span className="text-sm font-medium">Offers</span>
          </button>
          <button 
            onClick={() => setActiveTab && setActiveTab('orders')}
            className="flex items-center gap-2 bg-white bg-opacity-20 px-4 py-2 rounded-full whitespace-nowrap hover:bg-opacity-30 transition-all"
          >
            <Package size={16} />
            <span className="text-sm font-medium">Track Order</span>
          </button>
          <button 
            onClick={() => setShowSubscription(true)}
            className="flex items-center gap-2 bg-white bg-opacity-20 px-4 py-2 rounded-full whitespace-nowrap hover:bg-opacity-30 transition-all"
          >
            <span className="text-sm font-medium">🔔 Subscribe</span>
          </button>
        </div>
      </div>

      {/* Banner Carousel */}
      <div className="px-4 mt-4 mb-6 banner-carousel">
        <div className="relative overflow-hidden rounded-2xl shadow-lg">
          <div 
            className="flex transition-transform duration-500 ease-in-out"
            style={{ transform: `translateX(-${currentBanner * 100}%)` }}
          >
            {banners.map((banner) => (
              <div
                key={banner.id}
                className={`min-w-full bg-gradient-to-r ${banner.background} p-6 flex items-center justify-between`}
              >
                <div className="flex-1">
                  <span className="bg-white bg-opacity-30 text-white text-xs px-3 py-1 rounded-full font-medium mb-2 inline-block">
                    {banner.offer}
                  </span>
                  <h2 className="text-2xl font-bold text-white mb-1">
                    {banner.title}
                  </h2>
                  <p className="text-white text-opacity-90 text-sm mb-3">
                    {banner.subtitle}
                  </p>
                  <button 
                    onClick={() => {
                      const productsElement = document.querySelector('.products-section');
                      if (productsElement) {
                        productsElement.scrollIntoView({ behavior: 'smooth' });
                      }
                    }}
                    className="bg-white text-blue-600 px-4 py-2 rounded-lg text-sm font-semibold hover:bg-opacity-90 transition-all"
                  >
                    Shop Now
                  </button>
                </div>
                <div className="text-6xl ml-4">
                  {banner.icon}
                </div>
              </div>
            ))}
          </div>
          
          {/* Banner Indicators */}
          <div className="absolute bottom-3 left-1/2 transform -translate-x-1/2 flex gap-2">
            {banners.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentBanner(index)}
                className={`w-2 h-2 rounded-full transition-all ${
                  currentBanner === index 
                    ? 'bg-white w-6' 
                    : 'bg-white bg-opacity-50'
                }`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Products Grid */}
      <div className="p-4 products-section">
        <h2 className="text-xl font-bold mb-4 text-gray-800">Our Products</h2>
        
        <div className="grid grid-cols-2 gap-4">
          {filteredProducts.map(product => (
            <div key={product.id} className="bg-white rounded-xl shadow-md overflow-hidden">
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 flex items-center justify-center">
                <span className="text-6xl">{product.image}</span>
              </div>
              
              <div className="p-3">
                <h3 className="font-semibold text-gray-800 text-sm mb-1">{product.name}</h3>
                <p className="text-xs text-gray-500 mb-2">{product.description}</p>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-bold text-blue-600">₹{product.price}</span>
                  <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">
                    {product.fat} fat
                  </span>
                </div>
                
                <button
                  onClick={() => addToCart(product)}
                  className="w-full bg-blue-500 text-white py-2 rounded-lg text-sm font-medium hover:bg-blue-600 transition-colors flex items-center justify-center gap-1"
                >
                  <Plus size={16} />
                  Add to Cart
                </button>
                
                <button
                  onClick={() => setShowSubscription(product)}
                  className="w-full bg-green-500 text-white py-2 rounded-lg text-sm font-medium hover:bg-green-600 transition-colors mt-2"
                >
                  Subscribe & Save
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Floating Checkout Button */}
      {cart.length > 0 && getCartCount() > 0 && (
        <button
          onClick={openCheckout}
          className="fixed bottom-24 right-6 bg-gradient-to-r from-green-500 to-green-600 text-white px-6 py-4 rounded-full shadow-2xl hover:shadow-green-500/50 transition-all transform hover:scale-105 flex items-center gap-3 z-50"
        >
          <ShoppingCart size={24} />
          <div className="flex flex-col items-start">
            <span className="text-xs font-medium">Checkout Now</span>
            <span className="text-lg font-bold">{getCartCount()} {getCartCount() === 1 ? 'item' : 'items'}</span>
          </div>
        </button>
      )}
    </div>
  );
};

export default HomeScreen;